import React, { FC, useEffect, useState } from 'react';
import { ExaminationApiContextProvider } from '@higo/common/lib/features/ExaminationPage/providers';
import { useVisitExaminationPageModel } from '@higo/common/lib/features/ExaminationPage/hooks';
import { isNil } from 'rambda';
import { FormProvider, useForm } from 'react-hook-form';
import { coerceNumber } from '@higo/common/lib/utils';
import styled, { css } from 'styled-components';
import { media } from '@higo/ui/lib/theme';
import { useParams } from 'react-router-dom';
import { useCreateJWTAuthorizedAxios } from 'hooks/useCreateJWTAuthorizedAxios';
import { useExchangeSharingTokenForVisitJWTMutation } from 'pages/Shared/SharedExaminationPage/hooks/useExchangeSharingTokenForVisitJWTMutation';
import { SharedExaminationPageSkeleton } from 'pages/Shared/SharedExaminationPage/components/SharedExaminationPageSkeleton';
import {
  AbdominalAuscultationSection,
  AnamnesisSection,
  CoughAuscultationSection,
  HeartAuscultationSection,
  LungsAuscultationSection,
  SkinExaminationSectionForVisitsInPublic,
  ThroatExaminationSectionForVisitsInPublic,
  EarsExaminationSectionForVisitsInPublic,
  ExaminationPageSummarySection,
} from '@higo/common/lib/features/ExaminationPage/sections';
import {
  ExaminationPageHeader,
  ExaminationPageInfoBarForVisits,
  ExaminationPageOverview,
  ExaminationPageTocContainer,
} from '@higo/common/lib/features/ExaminationPage/components';
import { Heading, HorizontalDivider, LoaderIcon, LogoIcon } from '@higo/ui';
import { LayoutContextProvider } from '@higo/ui/lib/contexts';
import { ExaminationDoctorDiagnosisDTO } from '@higo/api/lib/models/examinationDoctorDiagnosisDTO';
import { useExaminationDiagnosisQuery } from '@higo/common/lib/features/ExaminationPage/hooks/queries';
import { FormattedMessage } from 'react-intl';
import { FullPageMessage } from 'components/FullPageMessage';
import { PhysicalExaminationType } from '@higo/api';
import { format } from 'date-fns';
import { parseJSON } from 'date-fns';
import { HeartRateMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/HeartRateMeasurementEntry';
import { TemperatureMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/TemperatureMeasurementEntry';
import { BloodPressureMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/BloodPressureMeasurementEntry';
import { BloodSaturationMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/BloodSaturationMeasurementEntry';
import { mapToExaminationPageDiagnosisFormValue } from '@higo/common/lib/features/ExaminationPage/models';

type ExaminationType = {
  examination: any;
  isCanceled: boolean;
  isStartedOnDevice?: boolean;
  examinationId: number;
};

export const sharedVisitPageUrlParams = {
  sharingToken: 'sharingToken',
};

export const ConstraintContainer = styled('div')(
  ({ theme }) => css`
    margin: 0 auto;
    width: 100%;
    max-width: 90rem; // 1440px
    padding: 0 2.5rem;
    ${theme.media.desktop.max} {
      padding: 0 1.25rem;
    }
    ${theme.media.desktop.max} {
      padding: 0 0.5rem;
    }
  `,
);

const ExamPageStyledHeader = styled(ExaminationPageHeader)`
  // height: 180px !important;
  ${media.desktop.min} {
    // height: 165px !important;
  }
`;

const VisitsContainer = styled.div`
  #heartRate-0:first-of-type {
    background-color: #eef0f4;
    padding: 5px;
    border-radius: 8px;
    height: 82px;
    max-width: 400px;
  }

  #temperature-0:first-of-type {
    background-color: #eef0f4;
    border-radius: 8px;
    height: 82px;
    padding: 5px;
    max-width: 400px;
  }

  #bloodPressure-0:first-of-type {
    background-color: #eef0f4;
    border-radius: 8px;
    height: 82px;
    padding: 5px;
    max-width: 400px;
  }

  #bloodSaturation-0:first-of-type {
    background-color: #eef0f4;
    border-radius: 8px;
    height: 82px;
    padding: 5px;
    max-width: 400px;
  }
`;
const MeasurementContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 1.5rem;
`;

const MeasurementItem = styled.div`
  flex: 1 1 auto;
  min-width: 80px;
`;

const ExamDate = styled.div`
  font-size: 12.8px;
  font-weight: 700;
  color: #72799c;
  margin-bottom: 0.5rem;
`;

const SharedExaminationPageContent: FC<{
  model: any;
  diagnosis?: ExaminationDoctorDiagnosisDTO;
}> = ({ model, diagnosis }) => {
  const [skinExams, setSkinExams] = useState<ExaminationType[]>([]);
  const [throatExams, setThroatExams] = useState<ExaminationType[]>([]);
  const [abdominalExams, setAbdominalExams] = useState<ExaminationType[]>([]);
  const [leftEarExams, setLeftEarExams] = useState<ExaminationType[]>([]);
  const [rightEarExams, setRightEarExams] = useState<ExaminationType[]>([]);
  const [coughExams, setCoughExams] = useState<ExaminationType[]>([]);
  const [lungsExams, setLungsExams] = useState<ExaminationType[]>([]);
  const [heartExams, setHeartExams] = useState<ExaminationType[]>([]);
  const [heartRateExams, setHeartRateExams] = useState<ExaminationType[]>([]);
  const [temperatureExams, setTemperatureExams] = useState<ExaminationType[]>(
    [],
  );
  const [bloodPressureExams, setBloodPressureExams] = useState<ExaminationType[]>(
    [],
  );
  const [bloodSaturationExams, setBloodSaturationExams] = useState<ExaminationType[]>(
    [],
  );

  useEffect(() => {
    const skin: ExaminationType[] = [];
    const throat: ExaminationType[] = [];
    const abdominal: ExaminationType[] = [];
    const leftEar: ExaminationType[] = [];
    const rightEar: ExaminationType[] = [];
    const cough: ExaminationType[] = [];
    const lungs: ExaminationType[] = [];
    const heart: ExaminationType[] = [];
    const heartRate: ExaminationType[] = [];
    const temperature: ExaminationType[] = [];
    const bloodPressure: ExaminationType[] = [];
    const bloodSaturation: ExaminationType[] = [];


    model?.examinations.forEach((examination: any, examinationId: number) => {
      const isCanceled =
        examination.physicalExaminationDetails[0].examStatus ===
          'CANCELED_BY_USER' ||
        examination.physicalExaminationDetails[0].examStatus ===
          'STARTED_ON_DEVICE';

      const examType = examination.physicalExaminationDetails[0].examType;

      switch (examType) {
        case PhysicalExaminationType.Skin:
          skin.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.Throat:
          throat.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.AbdominalAuscultation:
          abdominal.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.LeftEar:
          leftEar.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.RightEar:
          rightEar.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.Cough:
          cough.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.LungsAuscultation:
          lungs.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.HeartAuscultation:
          heart.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.HeartRateWav:
          heartRate.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.Temperature:
          temperature.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.BloodPressure:
          bloodPressure.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.BloodSaturation:
          bloodSaturation.push({ examination, isCanceled, examinationId });
          break;
        default:
          break;
      }
    });

    setSkinExams(skin);
    setThroatExams(throat);
    setAbdominalExams(abdominal);
    setLeftEarExams(leftEar);
    setRightEarExams(rightEar);
    setCoughExams(cough);
    setLungsExams(lungs);
    setHeartExams(heart);
    setHeartRateExams(heartRate);
    setTemperatureExams(temperature);
    setBloodPressureExams(bloodPressure);
    setBloodSaturationExams(bloodSaturation);
  }, [model]);

  const examDate = (date: Date | undefined) =>
    date && format(new Date(parseJSON(date)), 'dd/MM/yyyy HH:mm:ss');

  const methods = useForm({
    defaultValues: mapToExaminationPageDiagnosisFormValue(diagnosis),
  });

  useEffect(() => {
    if (diagnosis) {
      methods.reset(mapToExaminationPageDiagnosisFormValue(diagnosis));
    }
  }, [diagnosis, methods]);

  return !isNil(model) ? (
    <VisitsContainer>
      <ExamPageStyledHeader>
        <ConstraintContainer>
          <ExaminationPageOverview
            mode={model.mode}
            examinationId={model.details.id ?? 0}
            patient={model.details.patient}
            patientOverview={model.patientOverview}
            isVisitOverview
            isVisitEnd={model.details.status === 'ENDED'}
          />
        </ConstraintContainer>
      </ExamPageStyledHeader>

      <ConstraintContainer>
        <ExaminationPageInfoBarForVisits
          examinationCreateDate={model?.details?.createDate}
          partnerName={model.details.patient.partnerDetails?.name}
          executedBy={model.details.executedBy}
          hasOperatorData={false}
          isSharedPageForVisits
        />
      </ConstraintContainer>

      <FormProvider {...methods}>
        <ConstraintContainer>
          <AnamnesisSection
            examinationId={model.details.id ?? 0}
            createDate={model.details.createDate}
            mode={model.mode}
            isNotesCreateAndReadPermission={false}
            isNotesReadPermissionOnly={false}
            anamnesisTaggedQuestions={
              model.answeredAnamnesisSurvey?.taggedQuestions
            }
            generalTaggedQuestions={
              model.answeredGeneralSurvey?.taggedQuestions
            }
            hasCopyFeature
            hasToastWhenCopy={true}
            isVisitSection={true}
          />
        </ConstraintContainer>
        {temperatureExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage id="lifeSigns.temperature" /> exams
            </Heading>
          </ConstraintContainer>
        )}
        <ConstraintContainer>
          <MeasurementContainer>
            {temperatureExams.length > 0 &&
              temperatureExams
                .sort(
                  (a, b) =>
                    new Date(b.examination.sessionEndTime).getTime() -
                    new Date(a.examination.sessionEndTime).getTime(),
                )
                .slice(0, 10)
                .map(({ examination, isCanceled }, index) => {
                  return (
                    <MeasurementItem
                      key={examination.examinationId}
                      id={`temperature-${index}`}
                    >
                      <ExamDate>
                        {examDate(examination.sessionEndTime)}
                      </ExamDate>

                      <TemperatureMeasurementEntry
                        examinationId={examination.examinationId}
                        isAvailable={true}
                        isVisitSection
                        isCanceledStatus={isCanceled}
                      />
                    </MeasurementItem>
                  );
                })}
          </MeasurementContainer>
          {temperatureExams.length > 0 && <HorizontalDivider />}
        </ConstraintContainer>

        {bloodPressureExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage
                id="common.bloodPressure"
                defaultMessage="Blood pressure"
              />{' '}
              exams
            </Heading>
          </ConstraintContainer>
        )}
        <ConstraintContainer>
          <MeasurementContainer>
            {bloodPressureExams.length > 0 &&
              bloodPressureExams
                .sort(
                  (a, b) =>
                    new Date(b.examination.sessionEndTime).getTime() -
                    new Date(a.examination.sessionEndTime).getTime(),
                )
                .slice(0, 10)
                .map(({ examination, isCanceled }, index) => {
                  return (
                    <MeasurementItem
                      key={examination.examinationId}
                      id={`bloodPressure-${index}`}
                    >
                      <ExamDate>
                        {examDate(examination.sessionEndTime)}
                      </ExamDate>

                      <BloodPressureMeasurementEntry
                        examinationId={examination.examinationId}
                        isAvailable={true}
                        isVisitSection
                        isCanceledStatus={isCanceled}
                      />
                    </MeasurementItem>
                  );
                })}
          </MeasurementContainer>
          {bloodPressureExams.length > 0 && <HorizontalDivider />}
        </ConstraintContainer>

        {bloodSaturationExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage
                id="common.bloodSaturation"
                defaultMessage="Blood saturation"
              />{' '}
              exams
            </Heading>
          </ConstraintContainer>
        )}
        <ConstraintContainer>
          <MeasurementContainer>
            {bloodSaturationExams.length > 0 &&
              bloodSaturationExams
                .sort(
                  (a, b) =>
                    new Date(b.examination.sessionEndTime).getTime() -
                    new Date(a.examination.sessionEndTime).getTime(),
                )
                .slice(0, 10)
                .map(({ examination, isCanceled }, index) => {
                  return (
                    <MeasurementItem
                      key={examination.examinationId}
                      id={`bloodSaturation-${index}`}
                    >
                      <ExamDate>
                        {examDate(examination.sessionEndTime)}
                      </ExamDate>

                      <BloodSaturationMeasurementEntry
                        examinationId={examination.examinationId}
                        isAvailable={true}
                        isVisitSection
                        isCanceledStatus={isCanceled}
                      />
                    </MeasurementItem>
                  );
                })}
          </MeasurementContainer>
          {bloodSaturationExams.length > 0 && <HorizontalDivider />}
        </ConstraintContainer>

        {heartRateExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage id="lifeSigns.pulse" /> exams
            </Heading>
          </ConstraintContainer>
        )}
        <ConstraintContainer>
          <MeasurementContainer>
            {heartRateExams.length > 0 &&
              heartRateExams
                .sort(
                  (a, b) =>
                    new Date(b.examination.sessionEndTime).getTime() -
                    new Date(a.examination.sessionEndTime).getTime(),
                )
                .slice(0, 10)
                .map(({ examination, isCanceled }, index) => (
                  <MeasurementItem
                    key={examination.examinationId}
                    id={`heartRate-${index}`}
                  >
                    <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                    <HeartRateMeasurementEntry
                      isAvailable
                      examinationId={examination.examinationId}
                      isVisitSection
                      isCanceledStatus={isCanceled}
                    />
                  </MeasurementItem>
                ))}
          </MeasurementContainer>
          {heartRateExams.length > 0 && <HorizontalDivider />}
        </ConstraintContainer>

        {skinExams.length > 0 && (
          <ConstraintContainer>
            {' '}
            <Heading level={3}>
              <FormattedMessage id="examinationPage.section.skinExamination.title" />{' '}
            </Heading>
          </ConstraintContainer>
        )}
        {skinExams.length > 0 &&
          skinExams
            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer key={examination.examinationId}>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <SkinExaminationSectionForVisitsInPublic
                  isStatusCanceledByUser={isCanceled}
                  hasTogglingGalleryVideo={false}
                  examinationId={examination.examinationId}
                  isVisitSection
                />
              </ConstraintContainer>
            ))}
        {skinExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}

        {heartExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage id="examinationPage.section.heartAuscultation.title" />{' '}
            </Heading>
          </ConstraintContainer>
        )}

        {heartExams.length > 0 &&
          heartExams

            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer key={examination.examinationId}>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <HeartAuscultationSection
                  isStatusCanceledByUser={isCanceled}
                  examinationId={examination.examinationId}
                  patientModel={model.patient}
                  isVisitSection
                />
              </ConstraintContainer>
            ))}

        {heartExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}

        {lungsExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage id="examinationPage.section.lungAuscultation.title" />{' '}
            </Heading>
          </ConstraintContainer>
        )}

        {lungsExams.length > 0 &&
          lungsExams

            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer key={examination.examinationId}>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <LungsAuscultationSection
                  isStatusCanceledByUser={isCanceled}
                  examinationId={examination.examinationId}
                  patientModel={model.patient}
                  isVisitSection
                />
              </ConstraintContainer>
            ))}

        {lungsExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}

        {coughExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage id="examinationPage.section.coughExamination.title" />{' '}
            </Heading>
          </ConstraintContainer>
        )}
        {coughExams.length > 0 &&
          coughExams
            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <CoughAuscultationSection
                  isStatusCanceledByUser={isCanceled}
                  examinationId={examination.examinationId}
                  isVisitSection
                />
              </ConstraintContainer>
            ))}

        {coughExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}

        {leftEarExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              {/* <FormattedMessage id="examinationPage.section.earsExamination.title" /> */}
              Left ear examination{' '}
            </Heading>
          </ConstraintContainer>
        )}

        {leftEarExams.length > 0 &&
          leftEarExams
            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <EarsExaminationSectionForVisitsInPublic
                  isStatusCanceledByUser={isCanceled}
                  isVisitLeftEarExam
                  examinationId={examination.examinationId}
                  isPhysicalExaminationCompletedFn={
                    model.isPhysicalExaminationCompletedFn
                  }
                  isVisitSection
                />
              </ConstraintContainer>
            ))}

        {leftEarExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}

        {rightEarExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              {/* <FormattedMessage id="examinationPage.section.earsExamination.title" /> */}
              Right ear examination{' '}
            </Heading>
          </ConstraintContainer>
        )}

        {rightEarExams.length > 0 &&
          rightEarExams
            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <EarsExaminationSectionForVisitsInPublic
                  isStatusCanceledByUser={isCanceled}
                  isVisitRightEarExam
                  examinationId={examination.examinationId}
                  isPhysicalExaminationCompletedFn={
                    model.isPhysicalExaminationCompletedFn
                  }
                  isVisitSection
                />
              </ConstraintContainer>
            ))}

        {rightEarExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}

        {throatExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage id="examinationPage.section.throatExamination.title" />{' '}
            </Heading>
          </ConstraintContainer>
        )}

        {throatExams.length > 0 &&
          throatExams
            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <ThroatExaminationSectionForVisitsInPublic
                  isStatusCanceledByUser={isCanceled}
                  examinationId={examination.examinationId}
                  hasTogglingGalleryVideo={false}
                  isVisitSection
                />
              </ConstraintContainer>
            ))}

        {throatExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}

        {abdominalExams.length > 0 && (
          <ConstraintContainer>
            <Heading level={3}>
              <FormattedMessage id="examinationPage.section.abdominalAuscultation.title" />{' '}
            </Heading>
          </ConstraintContainer>
        )}

        {abdominalExams.length > 0 &&
          abdominalExams

            .sort(
              (a, b) =>
                new Date(b.examination.sessionEndTime).getTime() -
                new Date(a.examination.sessionEndTime).getTime(),
            )

            .map(({ examination, isCanceled }) => (
              <ConstraintContainer key={examination.examinationId}>
                <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                <AbdominalAuscultationSection
                  examinationId={examination.examinationId}
                  isVisitSection
                />
              </ConstraintContainer>
            ))}

        {abdominalExams.length > 0 && (
          <ConstraintContainer>
            <HorizontalDivider />
          </ConstraintContainer>
        )}
        <ConstraintContainer>
          <ExaminationPageSummarySection
            patientOverview={model.patientOverview}
            isReadOnly={true}
            isVisitSection
          ></ExaminationPageSummarySection>
        </ConstraintContainer>
      </FormProvider>
      <ConstraintContainer></ConstraintContainer>
    </VisitsContainer>
  ) : (
    <></>
  );
};

const SharedExaminationPageDataLoader: FC<{ id: number }> = ({ id }) => {
  const visitId = coerceNumber(id);
  const model = useVisitExaminationPageModel(visitId);

  const { data: diagnosisData, isLoading: isDiagnosisLoading } =
    useExaminationDiagnosisQuery(
      visitId,
      model?.isDiagnosisAvailable ?? false,
      false,
    );

  return isNil(model) || isDiagnosisLoading ? (
    <SharedExaminationPageSkeleton />
  ) : (
    <SharedExaminationPageContent model={model} diagnosis={diagnosisData} />
  );
};

const SharedVisitPage = () => {
  const { [sharedVisitPageUrlParams.sharingToken]: sharingToken } = useParams();

  const {
    data: jwtToken,
    mutate,
    isIdle,
    isLoading,
    isSuccess,
    isError,
  } = useExchangeSharingTokenForVisitJWTMutation();

  useEffect(() => {
    if (sharingToken && (isIdle || (!isSuccess && !isLoading))) {
      mutate(sharingToken);
    }
  }, [sharingToken, isIdle, isSuccess, isLoading, mutate]);

  const decode = (token?: string) => {
    try {
      if (!token) return undefined;
      const [, p] = token.split('.');
      return JSON.parse(atob(p.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return undefined;
    }
  };
  const claims = decode(jwtToken?.access_token);
  const visitId = claims?.visitId ?? claims?.visit_id;

  const authorizedAxios = useCreateJWTAuthorizedAxios(jwtToken?.access_token);

  // pokaż loader, dopóki mutacja nie skończyła
  if (isIdle || isLoading) {
    return (
      <FullPageMessage icon={<LoaderIcon />}>
        <FormattedMessage id="microsite.loadingExamination" />
      </FullPageMessage>
    );
  }

  // błąd wymiany tokena od backendu
  if (isError) {
    return (
      <FullPageMessage icon={<LogoIcon />}>
        <FormattedMessage id="microsite.invalidTokenProvided" />
      </FullPageMessage>
    );
  }

  // po sukcesie: walidacja wymaganych danych
  if (!visitId || !authorizedAxios) {
    return (
      <FullPageMessage icon={<LogoIcon />}>
        <FormattedMessage id="microsite.invalidTokenProvided" />
      </FullPageMessage>
    );
  }

  return (
    <LayoutContextProvider>
      <ExaminationApiContextProvider value={authorizedAxios}>
        <ExaminationPageTocContainer>
          {/* jeśli loader/model jest for examination, dostosuj do wizyty */}
          <SharedExaminationPageDataLoader id={visitId} />
        </ExaminationPageTocContainer>
      </ExaminationApiContextProvider>
    </LayoutContextProvider>
  );
};

export default SharedVisitPage;
